import React, { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { LocationOption } from 'Src/feature/provision/provisionService';

import { ProvisionItemCta } from './ProvisionItemCTA';
import { ProvisionItemList } from './ProvisionItemList';
import { useEmailWorkflow } from './useEmailWorkflow';
import { useLocation } from './useLocation';

import styles from './LocationSelector.module.scss';

class FormModel {
  search = '';
  constructor(part?: Partial<FormModel>) {
    if (part) Object.assign(this, part);
  }
}

const { Form, Field } = getTypedFormComponents<FormModel>();

const LocationSelector: FC = () => {
  const { t } = useTranslation();
  const workflow = useEmailWorkflow();
  const locationGetAll = useLocation.query.getAll({ orgId: workflow.data.organizationId as number });
  const form = useForm<FormModel>({ defaultValues: { ...new FormModel() } });
  const { search } = form.state.watch();

  const selectLocation = useCallback((locationId: number) => {
    workflow.updateData({ locationId });
    workflow.steps.canGoNext = true;
  }, [workflow]);

  let locations = [] as LocationOption[];
  if (!search || search.length === 0) {
    locations = workflow.data.locations;
  } else {
    locations = workflow.data.locations.filter((l) => JSON.stringify(l).toLowerCase().includes(search.toLowerCase()));
  }

  useEffect(() => {
    if (workflow.data.locations.length > 0) return;
    if (locationGetAll.isLoading) return;
    if (!locationGetAll.data || !locationGetAll.data.length) return;
    workflow.updateData({ locations: locationGetAll.data });
    // make a default selection if only one location exists
    if (locationGetAll.data.length === 1) {
      selectLocation(locationGetAll.data[0].id);
    }
    // only want to update when new data is available
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow.data.locations, locationGetAll.data, locationGetAll.isLoading]);


  useEffect(() => {
    workflow.steps.canGoNext = !!workflow.data.locationId;
    // only want to update when "locationId" changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow.data.locationId]);

  if (locationGetAll.isLoading) return null;

  return (
    <Flex className={styles.root} alignItems="center" fullHeight>
      <Copy variant="h6" color="secondary" value={t('location.selector.instruction')} />
      <Form usedForm={form} className={styles.searchFieldWrapper}>
        <Field
          type="search"
          name="search"
          id="location-select-search"
          placeholder={t('location.selector.search')}
        />
      </Form>

      <ProvisionItemList
        title={t('location.selector.orgLocation', { orgName: workflow.data.current.organization?.name })}
      >
        {locations.map((loc) => (
          <ProvisionItemCta
            key={loc.id}
            data-testid="list-item-cta"
            selected={workflow.data.locationId === loc.id}
            onClick={() => selectLocation(loc.id)}
            title={loc.name}
            subTitle={t('labels.fullAddress', loc.address)}
          />
        ))}
      </ProvisionItemList>
    </Flex>
  );
};

export default LocationSelector;
